import { useEffect, useState, useMemo } from 'react';
import './EncounterDateFilter.scss';
import { CalendarIcon, CalendarRangeIcon } from 'src/icons';
import { storeInLocal } from 'src/lib/storage';
import { SwapRightOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useAuth } from '@frontegg/react';
import { Popover } from 'antd';
import { analytics } from 'src/services';

export const EncounterDateFilter = ({
  currentDate,
  setCurrentDate,
  renderCalendar,
  handleDateFilterChange,
  resetDateFilter,
  setIsResetFiltersOn,
  setResetDateFilter,
  selectedFilter,
  setSelectedFilter,
  selectedDate,
  setSelectedDate,
  selectedDates,
  setSelectedDates,
  startDateRange,
  setStartDateRange,
  endDateRange,
  setEndDateRange,
}) => {
  const isMobile = window.innerWidth < 1260;
  const { user } = useAuth();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const monthNames = useMemo(
    () => [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    []
  );

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const shortMonthNames = useMemo(
    () => [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    []
  );

  const filterButtons = useMemo(
    () => ['Today', 'Yesterday', 'Past 7 days', 'All time'],
    []
  );

  const formatDateRange = (date) => {
    if (!date) return '';
    const formattedDate = new Date(date);
    return `${formattedDate.getDate()}${shortMonthNames[formattedDate.getMonth()]}`;
  };

  const handleMonthChange = (increment) => {
    const newDate = new Date(currentDate);
    newDate.setDate(1);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentDate(newDate);
  };
  const handleYearChange = (increment) => {
    const newDate = new Date(currentDate);
    newDate.setDate(1);
    newDate.setFullYear(newDate.getFullYear() + increment);
    setCurrentDate(newDate);
  };

  const handleDateFilter = (startDate, endDate, type) => {
    setSelectedDates([startDate, endDate]);
    if (type !== 'preset') {
      if (startDate && endDate) {
        setSelectedFilter(
          formatDateRange(startDate) + ' - ' + formatDateRange(endDate)
        );
      }
    } else {
      setSelectedFilter('');
    }
    localStorage.setItem(
      `${currentPractitioner?.org_uuid}_startDateRange`,
      formatDateRange(startDate)
    );
    localStorage.setItem(
      `${currentPractitioner?.org_uuid}_endDateRange`,
      formatDateRange(endDate)
    );

    if (startDate !== null && endDate !== null) {
      handleDateFilterChange([startDate, endDate]);
      setDropdownVisible(false);
    } else if (startDate) {
      setEndDateRange('');
    } else {
      handleDateFilterChange([]);
    }
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_selectedFilter`);
    storeInLocal(
      `${currentPractitioner?.org_uuid}_selectedDates`,
      JSON.stringify([startDate, endDate])
    );
  };

  const handleFilterButton = (filter) => {
    setIsResetFiltersOn(false);
    const today = new Date();
    if (filter === 'Today') {
      handleDateFilter(today, today, 'preset');
    } else if (filter === 'Yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      handleDateFilter(yesterday, yesterday, 'preset');
    } else if (filter === 'Past 7 days') {
      const sixDaysAgo = new Date(today);
      sixDaysAgo.setDate(today.getDate() - 6);
      handleDateFilter(sixDaysAgo, today, 'preset');
    } else if (filter === 'All time') {
      handleDateFilter('', '', 'preset');
    }
    setSelectedFilter(filter);
    setSelectedDates([null, null]);
    setDropdownVisible(false);
    localStorage.setItem(
      `${currentPractitioner?.org_uuid}_selectedFilter`,
      filter
    );
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_selectedDates`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_startDateRange`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_endDateRange`);
  };

  useEffect(() => {
    const storedFilter = localStorage.getItem(
      `${currentPractitioner?.org_uuid}_selectedFilter`
    );
    const storedDate = localStorage.getItem(
      `${currentPractitioner?.org_uuid}_selectedDates`
    );

    if (storedDate) {
      const parsedDates = JSON.parse(storedDate);
      setSelectedDates(parsedDates);
      handleDateFilter(parsedDates[0], parsedDates[1], '');
    } else if (storedFilter) {
      setSelectedFilter(storedFilter);
      handleFilterButton(storedFilter);
      localStorage.setItem(
        `${currentPractitioner?.org_uuid}_selectedFilter`,
        storedFilter
      );
    } else {
      setSelectedFilter('Today');
      localStorage.setItem(
        `${currentPractitioner?.org_uuid}_selectedFilter`,
        'Today'
      );
    }
  }, []);

  useEffect(() => {
    if (resetDateFilter) {
      const today = new Date();
      handleDateFilter(today, today, 'preset');
      setSelectedFilter('Today');
      handleFilterButton('Today');
      setResetDateFilter(false);
      localStorage.removeItem(
        `${currentPractitioner?.org_uuid}_startDateRange`
      );
      localStorage.removeItem(`${currentPractitioner?.org_uuid}_endDateRange`);
    }
  }, [resetDateFilter, setResetDateFilter]);

  const currentMonth = useMemo(() => {
    const month = new Date(currentDate);
    month.setMonth(month.getMonth());
    return month;
  }, [currentDate]);

  const nextMonth = useMemo(() => {
    const month = new Date(currentDate);
    month.setDate(1);
    month.setMonth(month.getMonth() + 1);
    return month;
  }, [currentDate]);

  const handlePrevMonthClick = (e) => {
    e.preventDefault();
    handleMonthChange(-1);
  };

  const handleNextMonthClick = (e) => {
    e.preventDefault();
    handleMonthChange(1);
  };

  const handlePrevYearClick = (e) => {
    e.preventDefault();
    handleYearChange(-1);
  };

  const handleNextYearClick = (e) => {
    e.preventDefault();
    handleYearChange(1);
  };

  const menu = useMemo(
    () => (
      <div className="custom-date-range-picker">
        <div className="buttons">
          <div className="button-row">
            {filterButtons.map((filter) => (
              <button
                type="button"
                key={filter}
                onClick={() => {
                  handleFilterButton(filter);
                  analytics.track('Clicked Date Filter In Aura', {
                    email: user?.email,
                  });
                }}
                className={
                  selectedFilter === filter ||
                  (!selectedDate === 'Custom…🗓️' && filter === 'Today')
                    ? 'selected'
                    : selectedDate === 'Custom…🗓️' &&
                        (filter === 'Yesterday' || filter === 'Past 7 days')
                      ? ''
                      : ''
                }
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-center">
          <div
            className={`${isMobile ? 'w-full' : 'w-60'} py-3 px-3 outline-1 outline-[#00d091] font-sans h-15 flex items-center rounded-xl outline-none`}
          >
            <input
              type="text"
              className="w-20 outline-none"
              placeholder="Start date"
              value={
                localStorage.getItem(
                  `${currentPractitioner?.org_uuid}_startDateRange`
                )
                  ? localStorage.getItem(
                      `${currentPractitioner?.org_uuid}_startDateRange`
                    )
                  : 'Start date'
              }
              readOnly
            />
            <div className="mr-2">
              <SwapRightOutlined />
            </div>
            <input
              type="text"
              className="w-20 outline-none"
              placeholder="End date"
              value={
                localStorage.getItem(
                  `${currentPractitioner?.org_uuid}_endDateRange`
                )
                  ? localStorage.getItem(
                      `${currentPractitioner?.org_uuid}_endDateRange`
                    )
                  : 'End date'
              }
              readOnly
            />
            <div className="mr-2">
              <CalendarRangeIcon />
            </div>
          </div>
        </div>
        <div className="calendars">
          <div className="calendar top-calendar">
            <div className="month-year">
              <div className="btn-first">
                <button type="button" onClick={handlePrevYearClick}>
                  &lt;&lt;
                </button>
                <button type="button" onClick={handlePrevMonthClick}>
                  &lt;
                </button>
              </div>
              <span>
                {monthNames[currentMonth.getMonth()]}{' '}
                {currentMonth.getFullYear()}
              </span>
            </div>
            <div className="mt-4">
              {renderCalendar(
                currentMonth,
                (start, end) => {
                  handleDateFilter(start, end, 'custom');
                  if (!end) {
                    setSelectedFilter(selectedFilter);
                  }
                },
                selectedDates
              )}
            </div>
          </div>
          <div className="calendar bottom-calendar">
            <div className="secmonth-year">
              <span>
                {monthNames[nextMonth.getMonth()]} {nextMonth.getFullYear()}
              </span>
              <div className="btn-datefilter">
                <button type="button" onClick={handleNextMonthClick}>
                  &gt;
                </button>
                <button type="button" onClick={handleNextYearClick}>
                  &gt;&gt;
                </button>
              </div>
            </div>
            <div className="mt-4">
              {renderCalendar(
                nextMonth,
                (start, end) => {
                  handleDateFilter(start, end, 'custom');
                  if (!end) {
                    setSelectedFilter(selectedFilter);
                  }
                },
                selectedDates
              )}
            </div>
          </div>
        </div>
      </div>
    ),
    [
      selectedFilter,
      currentDate,
      selectedDates,
      handleDateFilter,
      handlePrevYearClick,
      handlePrevMonthClick,
      handleNextMonthClick,
      handleNextYearClick,
      monthNames,
      currentMonth,
      nextMonth,
      renderCalendar,
    ]
  );

  return (
    <Popover
      open={dropdownVisible}
      styles={{
        body: {
          padding: '0.1px',
          borderRadius: '38px',
        },
      }}
      content={menu}
      onOpenChange={setDropdownVisible}
      trigger="click"
    >
      <div className="w-32 h-10 p-1 rounded-xl cursor-pointer bg-white drop-shadow-sm flex items-center justify-stretch tree-dropdown">
        <button
          type="button"
          className="ml-1 btn-text flex items-center gap-2 whitespace-nowrap"
        >
          <CalendarIcon
            className={
              selectedFilter === 'Yesterday' ||
              selectedFilter === 'Past 7 days' ||
              selectedFilter === 'Today' ||
              selectedFilter === 'All time'
                ? 'mr-2 text-black'
                : 'text-black'
            }
          />
          <div id="DateFilter">{selectedFilter}</div>
        </button>
      </div>
    </Popover>
  );
};
